import { useEffect, useRef, useState } from 'react'

import type { EditHistoryState } from '@fit-editor/fit-core'

import { selectTerrainState, useActivityStore } from '@/store/activity-store'
import type { LoadedActivity, TerrainOverlayState } from '@/store/activity-store'
import { sampleTerrainForHistoryAsync } from '@/worker/fit-client'
import type { TerrainSampleResult } from '@/worker/fit-client'

export interface TerrainOverlay {
  /** Terrain elevation per record of `loaded`'s *current* activity (index-aligned); `null` = no value. */
  values: (number | null)[]
  /** Tiles the current route needs that aren't loaded yet (e.g. after a GPS edit moved points). */
  unresolvedTiles: number
}

export interface TerrainOverlayResult {
  terrain: TerrainOverlayState
  /** `null` while no overlay has been loaded, or while (re)sampling is in flight. */
  overlay: TerrainOverlay | null
  /** Set when sampling the cache failed (a Worker RPC failure). */
  error: string | null
}

interface Entry {
  history: EditHistoryState
  result: TerrainSampleResult | null
  error: string | null
}

/**
 * Samples the worker's cached terrain tiles for `loaded`'s current
 * activity once an overlay has been loaded for it, and again whenever its
 * history changes (an edit, undo/redo) or more tiles are loaded. Keyed by
 * GPS position, not timestamp, so it stays correct after crop/shift/GPS
 * edits -- records whose tiles aren't cached come back `null`, and
 * `unresolvedTiles` tells the UI to *offer* loading them (never
 * auto-fetches: sampling is cache-only).
 *
 * Mirrors `useCurrentActivity`'s request-id-guarded, "compare the latest
 * entry's identity against the current inputs" pattern so a stale
 * response is never shown. Pass `undefined` to skip entirely (e.g. a
 * closed dialog).
 */
export function useTerrainOverlay(loaded: LoadedActivity | undefined): TerrainOverlayResult {
  const terrain = useActivityStore((s) => selectTerrainState(s, loaded?.id))
  const history = loaded?.history
  const enabled = history != null && terrain.source != null && terrain.status !== 'error'
  const revision = terrain.revision

  const [entry, setEntry] = useState<Entry | null>(null)
  const requestIdRef = useRef(0)

  useEffect(() => {
    if (!enabled || history == null) return
    const requestId = ++requestIdRef.current
    sampleTerrainForHistoryAsync(history)
      .then((result) => {
        if (requestIdRef.current === requestId) {
          setEntry({ history, result, error: null })
        }
      })
      .catch((error: unknown) => {
        if (requestIdRef.current === requestId) {
          setEntry({
            history,

            result: null,
            error: error instanceof Error ? error.message : String(error),
          })
        }
      })
  }, [enabled, history, revision])

  // An entry from an older `revision` for the *same* history is still
  // correct (its tiles are a subset of the cache's current contents), so
  // it keeps showing while the post-load resample is in flight -- this
  // avoids the overlay blinking out (and the chart's full-option replace
  // that would cause) after "Load missing terrain tiles".
  const matches = enabled && entry != null && entry.history === history
  return {
    terrain,
    overlay: matches ? entry.result : null,
    error: matches ? entry.error : null,
  }
}
